import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiGet, apiPost } from '../../lib/api';
import { useCurrency } from '../../context/CurrencyContext';
import InlineCurrencyPicker from '../InlineCurrencyPicker';
import { 
  X, Calendar, Clock, CheckCircle, CreditCard, User, Shield, Info,
  ChevronLeft, ChevronRight, Package, FileText, Loader2
} from 'lucide-react';

export default function ServiceBookingModal({ 
  isOpen, 
  onClose, 
  service, 
  creative, 
  packages = [],
  selectedPackage: initialPackage = null 
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatPrice, convert, currency: userCurrency } = useCurrency();
  
  // Wizard state - Start at step 2 since packages are hidden for now
  const [step, setStep] = useState(2);
  const [selectedPackage, setSelectedPackage] = useState(initialPackage);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [requirements, setRequirements] = useState('');
  const [contactPreference, setContactPreference] = useState('email');
  
  // API state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [needLogin, setNeedLogin] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Original service currency for conversion
  const serviceCurrency = service?.currency || creative?.currency || 'SEK';
  
  // Calculate pricing - NO platform fees
  const basePrice = service?.hourly_rate || service?.price || selectedPackage?.price || service?.package_price || service?.base_price || 0;
  const totalPrice = Number(basePrice);
  const deliveryDays = selectedPackage?.delivery_days || service?.delivery_days || 7;
  
  // Convert prices to user's selected currency
  const convertedBasePrice = convert(basePrice, serviceCurrency, userCurrency);
  const convertedTotalPrice = convert(totalPrice, serviceCurrency, userCurrency);

  // Set initial package if available
  useEffect(() => {
    if (initialPackage) {
      setSelectedPackage(initialPackage);
    } else if (packages.length > 0) {
      setSelectedPackage(packages[0]);
    }
  }, [initialPackage, packages]);

  // Load available slots when creative changes
  useEffect(() => {
    if (!creative?.id || !isOpen) return;
    let active = true;
    
    async function loadSlots() {
      setLoadingSlots(true);
      try {
        const now = new Date();
        const horizon = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
        const res = await apiGet('/availabilities', {
          creative_id: creative.id,
          ends_after: now.toISOString(),
          starts_before: horizon.toISOString(),
          limit: 100,
        });
        const list = res?.data?.availabilities || res?.availabilities || [];
        const filtered = list
          .filter((it) => (it?.kind || 'available') === 'available')
          .filter((it) => new Date(it.start_at) > now)
          .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
        if (active) setAvailableSlots(filtered);
      } catch (e) {
        console.error('Failed to load slots:', e);
      } finally {
        if (active) setLoadingSlots(false);
      }
    }
    loadSlots();
    return () => { active = false; };
  }, [creative?.id, isOpen]);

  // Handle payment success redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('paid') === '1') {
      setResult({ status: 'confirmed', message: 'Payment successful! Your booking is confirmed.' });
      setStep(4);
    }
    if (params.get('canceled') === '1') {
      setError('Payment was cancelled. You can try again.');
    }
  }, [location.search]);

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    
    try {
      let userId = null;
      try {
        const me = await apiGet('/users/me');
        userId = me?.data?.user?.id || me?.user?.id || me?.data?.id || me?.id || null;
      } catch { /* not logged in */ }
      
      if (!userId) {
        setError('Please login to book');
        setNeedLogin(true);
        setLoading(false);
        return;
      }

      const startAt = selectedDate && selectedTime 
        ? new Date(`${selectedDate}T${selectedTime}:00`).toISOString()
        : new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      
      const payload = {
        service_id: service?.id,
        creative_id: creative?.id,
        meeting_type: 'hourly',
        scheduled_at: startAt,
        hours: deliveryDays * 8,
        notes: requirements,
        client_requirements: {
          description: requirements,
          contact_preference: contactPreference,
        },
      };

      const res = await apiPost('/payments/quick-pay', payload);

      if (res?.data?.checkout_url || res?.checkout_url) {
        window.location.assign(res?.data?.checkout_url || res?.checkout_url);
        return;
      }

      setResult({
        booking: res?.data?.booking || res?.booking,
        pricing: res?.data?.pricing || res?.pricing,
        status: 'confirmed',
        message: 'Your booking has been submitted!',
      });
      setStep(4);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const loginUrl = `/login?redirect=${encodeURIComponent(location.pathname + (location.search || ''))}`;

  if (!isOpen) return null;

  // Success screen
  if (step === 4 && result?.status === 'confirmed') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="bg-card-bg rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-card-border animate-in fade-in zoom-in duration-200">
          <div className="p-6 text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-green-500/10 rounded-full flex items-center justify-center">
              <CheckCircle size={32} className="text-green-500" />
            </div>
            <h2 className="text-xl font-semibold text-primary-text">Booking Confirmed!</h2>
            <p className="text-secondary-text">{result.message}</p>
            
            {result.booking && (
              <div className="bg-subtle-bg rounded-lg p-4 text-left space-y-2">
                <div className="text-sm">
                  <span className="text-secondary-text">Booking #: </span>
                  <span className="font-mono text-primary-text">{result.booking.booking_number}</span>
                </div>
                <div className="text-sm">
                  <span className="text-secondary-text">Service: </span>
                  <span className="text-primary-text">{service?.title}</span>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 rounded-lg border border-card-border text-primary-text hover:bg-subtle-bg transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => navigate('/dashboard/bookings')}
                className="flex-1 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                View Bookings
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card-bg rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden border border-card-border animate-in fade-in zoom-in duration-200 flex flex-col">
        {/* Header */}
        <div className="border-b border-card-border px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Package size={20} className="text-blue-500" />
              </div>
              <div>
                <h2 className="font-semibold text-primary-text">
                  {t('booking.title', { defaultValue: 'Book Service' })}
                </h2>
                <p className="text-sm text-secondary-text line-clamp-1">{service?.title}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-subtle-bg transition-colors"
              aria-label="Close"
            >
              <X size={20} className="text-secondary-text" />
            </button>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mt-4">
            {[
              { num: 1, label: 'Details' },
              { num: 2, label: 'Confirm' },
            ].map((s, i) => {
              const actualStep = s.num + 1;
              return (
                <div key={s.num} className="flex items-center">
                  <div className={`flex items-center gap-2 ${step >= actualStep ? 'text-blue-600' : 'text-secondary-text'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step > actualStep ? 'bg-blue-600 text-white' :
                      step === actualStep ? 'bg-blue-600 text-white' :
                      'bg-subtle-bg border border-card-border'
                    }`}>
                      {step > actualStep ? <CheckCircle size={16} /> : s.num}
                    </div>
                    <span className="text-sm">{s.label}</span>
                  </div>
                  {i < 1 && (
                    <div className={`w-12 h-0.5 mx-2 ${step > actualStep ? 'bg-blue-600' : 'bg-card-border'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 2: Requirements */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-primary-text">
                {t('booking.details', { defaultValue: 'Booking Details' })}
              </h3>

              {/* Date/Time Selection */}
              <div>
                <label className="block text-sm font-medium text-secondary-text mb-2">
                  Preferred Start Date & Time
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                    className="input w-full"
                  />
                  <input
                    type="time"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    className="input w-full"
                  />
                </div>
                <p className="text-xs text-secondary-text mt-1">
                  Select when you'd like the creative to start
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-text mb-2">
                  {t('booking.requirements', { defaultValue: 'Special Requests or Requirements' })}
                </label>
                <textarea
                  value={requirements}
                  onChange={(e) => setRequirements(e.target.value)}
                  rows={3}
                  className="input w-full resize-none"
                  placeholder="Add any specific requirements..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-text mb-2">
                  {t('booking.contactPreference', { defaultValue: 'Contact Preference' })}
                </label>
                <div className="flex gap-4">
                  {[
                    { value: 'email', label: 'Email' },
                    { value: 'phone', label: 'Phone' },
                    { value: 'both', label: 'Both' },
                  ].map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="contactPreference"
                        value={opt.value}
                        checked={contactPreference === opt.value}
                        onChange={(e) => setContactPreference(e.target.value)}
                        className="text-blue-600"
                      />
                      <span className="text-sm text-primary-text">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="bg-blue-500/5 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Info size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-secondary-text">
                    After booking, you'll receive contact details to coordinate directly with the creative.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Confirm & Pay */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-primary-text">
                {t('booking.payment', { defaultValue: 'Confirm & Pay' })}
              </h3>

              {/* Order Summary */}
              <div className="bg-subtle-bg rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-3 pb-3 border-b border-card-border">
                  {creative?.user?.avatar_url || creative?.avatar_url ? (
                    <img 
                      src={creative?.user?.avatar_url || creative?.avatar_url} 
                      alt="" 
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <User size={18} className="text-blue-500" />
                    </div>
                  )}
                  <div>
                    <div className="font-medium text-primary-text">
                      {creative?.user?.display_name || creative?.display_name || creative?.name}
                    </div>
                    <div className="text-xs text-secondary-text">
                      {creative?.professional_title}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-secondary-text">Service</span>
                    <span className="text-primary-text">{service?.title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-text">Delivery</span>
                    <span className="text-primary-text">{deliveryDays} days</span>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="rounded-lg p-4 border border-card-border bg-subtle-bg">
                {/* Currency Picker */}
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-card-border">
                  <span className="text-sm text-secondary-text">Prices shown in</span>
                  <InlineCurrencyPicker />
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-secondary-text">Service Cost</span>
                    <span className="text-primary-text">{formatPrice(convertedBasePrice, userCurrency)}</span>
                  </div>
                  <div className="border-t border-card-border pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="font-semibold text-primary-text">Total</span>
                      <span className="text-xl font-bold text-primary-text">
                        {formatPrice(convertedTotalPrice, userCurrency)}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-3 flex items-center gap-2 text-xs text-emerald-600">
                  <CheckCircle size={14} />
                  <span>No hidden fees - full amount goes to the creative</span>
                </div>
              </div>

              {/* Money Back Guarantee */}
              <div className="flex items-center gap-2 p-3 bg-green-500/5 border border-green-200 dark:border-green-800 rounded-lg">
                <Shield size={18} className="text-green-500 flex-shrink-0" />
                <p className="text-sm text-secondary-text">
                  <span className="font-medium text-green-600">Money Back Guarantee</span>
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600">
                  {error}
                </div>
              )}

              {needLogin && (
                <button
                  onClick={() => navigate(loginUrl)}
                  className="w-full py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
                >
                  Log in to continue
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <div className="border-t border-card-border px-6 py-4 flex items-center justify-between bg-subtle-bg flex-shrink-0">
          <button
            onClick={() => step > 2 ? setStep(step - 1) : onClose()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-card-border text-primary-text hover:bg-card-bg transition-colors"
          >
            <ChevronLeft size={18} />
            {step > 2 ? 'Back' : 'Cancel'}
          </button>

          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
            >
              Continue
              <ChevronRight size={18} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading || needLogin}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard size={18} />
                  Pay {formatPrice(convertedTotalPrice, userCurrency)}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
